import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { usePublicStats } from "../components/DynamicGrantCount";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Lightning, CheckCircle, CurrencyDollar, ChartLineUp, Bell, Sparkle, ArrowLeft, Ticket, WarningCircle, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import EmailAuthModal from "../components/EmailAuthModal";

const Pricing = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(null);
  const [audience, setAudience] = useState("nonprofit"); // nonprofit | business
  const publicStats = usePublicStats();
  const grantCountStr = publicStats?.total_active ? String(publicStats.total_active) : "100";
  const scoutCountStr = publicStats?.corporate_scouts ? String(publicStats.corporate_scouts) : "30";

  const handleSubscribe = async (plan) => {
    if (!user) {
      login();
      return;
    }

    setLoading(plan);
    try {
      // 'free' isn't a real subscribe target; only starter & pro hit Stripe
      if (plan === "free") {
        navigate("/grants");
        setLoading(null);
        return;
      }
      const res = await axios.post(`${API}/subscriptions/subscribe`, { tier: plan });
      // Redirect to Stripe Checkout (subscription mode — recurring monthly billing)
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setLoading(null);
    }
  };

  const nonprofitPlans = [
    {
      id: "free",
      name: "Free",
      price: 0,
      description: "Get started with grant applications",
      features: [
        "1 Flash Draft per month",
        `Browse all ${grantCountStr} verified SC grants`,
        "Best Match scoring",
        "Bookmark grants",
        "3 applications per month",
        "Application tracker",
        "Cross-Collaboration discovery (browse only)"
      ],
      cta: user?.subscription_tier === "free" || !user?.subscription_tier ? "Current Plan" : "Get Started",
      disabled: user?.subscription_tier === "free" || !user?.subscription_tier
    },
    {
      id: "starter",
      name: "Starter",
      price: 19.99,
      description: "Built for small SC nonprofits",
      features: [
        "Everything in Free, plus:",
        "5 Flash Drafts / month",
        "Unlimited applications",
        "Cross-Collaboration: connect & message",
        "Join Joint Application Groups",
        "Calendar view + deadline reminders",
        "Auto-saving across devices",
        "Email support"
      ],
      cta: user?.subscription_tier === "starter" ? "Current Plan" : "Choose Starter",
      disabled: user?.subscription_tier === "starter"
    },
    {
      id: "pro",
      name: "Premium",
      price: 49.99,
      description: "Complete grant management",
      popular: true,
      features: [
        "Everything in Starter, plus:",
        "Unlimited Flash Drafts",
        `Corporate Scout (${scoutCountStr} verified SC funders)`,
        "Post-Grant Manager",
        "Budget tracking & expense logs",
        "Milestone management",
        "Impact Dashboard",
        "Create Joint Application Groups",
        "Priority support"
      ],
      cta: user?.subscription_tier === "pro" ? "Current Plan" : "Upgrade to Premium",
      disabled: user?.subscription_tier === "pro"
    }
  ];

  // Business audience: clean two-tier ladder focused on what actually works for
  // for-profit small businesses & franchisees — Local Sponsorship discovery
  // (free) leading into Local Hero ($5K/yr named recognition).
  // The $19.99 grant-chasing Starter and $49.99 Premium tiers are nonprofit-only
  // because their features (Flash Drafts, federal grants tracker, etc.) require
  // 501(c)(3) eligibility on the receiving end.
  const businessPlans = [
    {
      id: "free",
      name: "Local Sponsor",
      price: 0,
      description: "Discover and book local nonprofit sponsorships in your county",
      features: [
        "Browse Local Sponsorship Opportunities (no limit)",
        "Filter by county, industry, budget",
        "Express interest in seconds — nonprofit emails you directly",
        "Supplier-diversity directory (Boeing, BMW, Duke, Michelin)",
        "Cross-collab visibility with SC nonprofits"
      ],
      cta: user?.subscription_tier === "free" || !user?.subscription_tier ? "Current Plan" : "Get Started",
      disabled: user?.subscription_tier === "free" || !user?.subscription_tier
    },
    {
      id: "local_hero",
      name: "Local Hero",
      price: "$5K/yr",
      isCustom: true,
      ctaUrl: "/local-hero",
      popular: true,
      description: "Named, branded annual partnership with one or more SC nonprofits",
      features: [
        "Everything in Local Sponsor, plus:",
        "Named recognition on the nonprofit's site, events, and materials",
        "Custom Local Hero microsite for your business",
        "Quarterly Impact PDF (tax-friendly summary)",
        "Leaderboard placement among SC Local Heroes",
        "Digital badge for your website & social proof",
        "Concierge introductions to 3 nonprofits matching your industry",
        "Annual renewal call with the FlashGrants team"
      ],
      cta: "Learn About Local Hero",
      disabled: false
    }
  ];

  const plans = (audience === "business" || audience === "franchisee") ? businessPlans : nonprofitPlans;

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      {user ? (
        <DashboardLayout>
          <PricingContent 
            plans={plans} 
            handleSubscribe={handleSubscribe} 
            loading={loading}
            user={user}
            audience={audience}
            setAudience={setAudience}
          />
        </DashboardLayout>
      ) : (
        <>
          <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-zinc-800/50">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
              <div 
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => navigate("/")}
              >
                <Lightning weight="fill" className="w-8 h-8 text-emerald-500" />
                <span className="text-xl font-bold tracking-tight">FlashGrants</span>
              </div>
              <Button 
                onClick={login}
                className="bg-emerald-500 text-black font-bold rounded-sm hover:bg-emerald-400"
                data-testid="login-btn"
              >
                Sign In
              </Button>
            </div>
          </header>
          <div className="pt-20 px-6">
            <PricingContent 
              plans={plans} 
              handleSubscribe={handleSubscribe} 
              loading={loading}
              user={user}
              audience={audience}
              setAudience={setAudience}
            />
          </div>
        </>
      )}
    </div>
  );
};

const PricingContent = ({ plans, handleSubscribe, loading, user, audience, setAudience }) => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  const [trialLoading, setTrialLoading] = useState(false);
  const [cancelTrialLoading, setCancelTrialLoading] = useState(false);
  const [showAuthForTrial, setShowAuthForTrial] = useState(false);

  const loadTrialInfo = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/trial/info`);
      setTrialInfo(res.data);
    } catch (e) { /* ignore */ }
  };

  // Load trial info on mount
  useEffect(() => { loadTrialInfo(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.user_id]);

  const handleStartTrial = async () => {
    if (!user) {
      // Logged-out user clicked Start Trial — capture intent + prompt sign-up.
      try { localStorage.setItem("pending_trial_start", "1"); } catch { /* ignore */ }
      setShowAuthForTrial(true);
      return;
    }
    setTrialLoading(true);
    try {
      // No-card trial path (default). User gets instant 14-day Premium with zero friction.
      // Trial auto-expires to Free on Day 14 via `trial_expiry_task`; user can add a card
      // any time to keep Premium going past then.
      const res = await axios.post(`${API}/trial/start`);
      if (res.data?.trial_active) {
        toast.success("Trial activated! You have 14 days of Premium — no card required.");
        if (refreshUser) await refreshUser();
        loadTrialInfo();
        navigate("/grants");
        return;
      }
      // Defensive fallback: if response shape is unexpected, surface a generic error
      toast.error("Trial didn't activate cleanly — please refresh and try again.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start trial");
    } finally {
      setTrialLoading(false);
    }
  };

  const handleCancelTrial = async () => {
    setCancelTrialLoading(true);
    try {
      const res = await axios.post(`${API}/trial/cancel`);
      toast.success(res.data.message);
      if (refreshUser) refreshUser();
      loadTrialInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel trial");
    } finally {
      setCancelTrialLoading(false);
    }
  };

  const canStartTrial = !user
    || (
      !user.subscription_permanent
      && user.subscription_tier !== "pro"
      && !(trialInfo && ["active","ended","cancelled","converted"].includes(trialInfo.trial_status))
    );

  // Auto-resume the trial flow when:
  //  - user just authenticated (via Google OAuth or email signup)
  //  - and EITHER `?start_trial=1` is in the URL, OR `pending_trial_start` flag is set in localStorage.
  // Kicks off the Stripe SetupIntent checkout automatically so the visitor never
  // has to figure out where to click after sign-up.
  useEffect(() => {
    if (!user) return;
    let intent = false;
    try { intent = localStorage.getItem("pending_trial_start") === "1"; } catch { /* ignore */ }
    if (!intent && searchParams.get("start_trial") !== "1") return;
    // Only auto-fire if the user truly can start a trial.
    // We wait one tick for trialInfo to load so we don't accidentally trigger
    // for users who already have an active/ended trial.
    if (trialInfo === null) return;
    const eligible = !user.subscription_permanent
      && user.subscription_tier !== "pro"
      && !(["active","ended","cancelled","converted"].includes(trialInfo?.trial_status));
    if (!eligible) {
      // Clear the flag/param so we don't loop on next page load.
      try { localStorage.removeItem("pending_trial_start"); } catch { /* ignore */ }
      if (searchParams.get("start_trial")) {
        searchParams.delete("start_trial");
        setSearchParams(searchParams, { replace: true });
      }
      return;
    }
    try { localStorage.removeItem("pending_trial_start"); } catch { /* ignore */ }
    if (searchParams.get("start_trial")) {
      searchParams.delete("start_trial");
      setSearchParams(searchParams, { replace: true });
    }
    toast.success("Welcome to FlashGrants! Setting up your 14-day Premium trial…");
    handleStartTrial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, trialInfo]);

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      const res = await axios.post(`${API}/subscriptions/cancel`);
      toast.success(res.data.message);
      setShowCancelConfirm(false);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancelLoading(false);
    }
  };

  const redeemPromo = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }
    if (!user) {
      toast.error("Please sign in first to redeem a promo code");
      return;
    }
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo/redeem`, { code: promoCode });
      toast.success(res.data.message);
      setPromoCode("");
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem promo code");
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto py-8" data-testid="pricing-page">
      {/* Back Button */}
      {user && (
        <Button 
          variant="ghost" 
          onClick={() => navigate("/dashboard")}
          className="text-zinc-400 hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      )}

      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 badge-emerald rounded-sm mb-4">
          <CurrencyDollar weight="fill" className="w-3 h-3" />
          <span>PRICING</span>
        </div>
        <h1 className="text-4xl font-bold tracking-tight mb-4">
          Choose Your Plan
        </h1>
        <p className="text-zinc-400 max-w-2xl mx-auto">
          {audience === "franchisee"
            ? "Most franchise agreements require documented community giving. Discover SC nonprofits in your territory for free, then upgrade to Local Hero for audit-ready quarterly Impact PDFs your franchisor can verify."
            : audience === "business"
            ? "Discover SC nonprofits that need local sponsors. Browse for free, then upgrade to a named Local Hero partnership when you're ready to commit."
            : "Start free and scale as your nonprofit grows. Upgrade anytime to unlock more Flash Drafts and powerful post-grant management tools."}
        </p>

        {/* Audience switcher */}
        <div className="mt-8 inline-flex flex-wrap items-center justify-center bg-zinc-900 border border-zinc-800 rounded-md p-1 gap-1" data-testid="pricing-audience-switcher">
          <button
            onClick={() => setAudience && setAudience("nonprofit")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold transition ${
              audience === "nonprofit"
                ? "bg-emerald-500 text-black"
                : "text-zinc-300 hover:text-white"
            }`}
            data-testid="pricing-audience-nonprofit"
          >
            Nonprofits
          </button>
          <button
            onClick={() => setAudience && setAudience("business")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold transition ${
              audience === "business"
                ? "bg-emerald-500 text-black"
                : "text-zinc-300 hover:text-white"
            }`}
            data-testid="pricing-audience-business"
          >
            Small Business
          </button>
          <button
            onClick={() => setAudience && setAudience("franchisee")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold transition ${
              audience === "franchisee"
                ? "bg-emerald-500 text-black"
                : "text-zinc-300 hover:text-white"
            }`}
            data-testid="pricing-audience-franchisee"
          >
            Franchisees
          </button>
        </div>
      </div>

      {/* Franchisee compliance callout */}
      {audience === "franchisee" && (
        <Card className="bg-emerald-500/5 border-emerald-500/30 border max-w-3xl mx-auto" data-testid="franchisee-compliance-callout">
          <CardContent className="py-5">
            <div className="flex items-start gap-3">
              <Info weight="fill" className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div className="space-y-1.5">
                <p className="text-sm text-zinc-200 font-semibold">Built for franchise community-giving compliance.</p>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Local Hero turns your sponsorships into <strong className="text-zinc-200">audit-ready quarterly Impact PDFs</strong> — verifiable nonprofit acknowledgement letters, dollar amounts, and recipient organization details that corporate HQ can file without follow-up. Used by SC franchise owners across QSR, hospitality, retail, and home services brands.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Promo Code Section - Prominent placement */}
      {user && user.subscription_tier !== "pro" && (
        <Card className="bg-emerald-500/5 border-emerald-500/30 border-2" data-testid="promo-code-section">
          <CardContent className="py-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="w-12 h-12 rounded-sm bg-emerald-500/15 flex items-center justify-center">
                  <Ticket weight="duotone" className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="font-bold text-white">Have a promo code?</p>
                  <p className="text-sm text-zinc-400">Redeem it for free Premium access</p>
                </div>
              </div>
              <div className="flex gap-2 flex-1 w-full sm:w-auto">
                <Input
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && redeemPromo()}
                  className="bg-zinc-900 border-zinc-700 text-white uppercase tracking-wider font-mono text-lg h-11"
                  data-testid="promo-code-input"
                />
                <Button
                  onClick={redeemPromo}
                  disabled={promoLoading || !promoCode.trim()}
                  className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm flex-shrink-0 h-11 px-6"
                  data-testid="redeem-promo-btn"
                >
                  {promoLoading ? "Redeeming..." : "Redeem"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Show promo status if user has active promo */}
      {user?.promo_code && user?.promo_expires_at && (
        <Card className="bg-emerald-500/5 border-emerald-500/20" data-testid="promo-status">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Ticket weight="fill" className="w-5 h-5 text-emerald-400" />
              <p className="text-sm text-zinc-300">
                Premium active via promo code <span className="font-mono font-bold text-emerald-400">{user.promo_code}</span> — 
                expires {new Date(user.promo_expires_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trial CTA — shown for users who can start the trial */}
      {canStartTrial && (
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-2 border-emerald-500/40" data-testid="trial-cta">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkle weight="fill" className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-lg font-bold">Try Premium Free for 14 Days</h3>
                  <span className="inline-block text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-sm border border-emerald-500/40" data-testid="no-card-badge">
                    No credit card
                  </span>
                </div>
                <p className="text-sm text-zinc-300 mb-2">
                  Full Premium access. <span className="text-emerald-400 font-semibold">No card. No auto-charge. No surprises.</span> Your account drops back to Free on Day 15 unless you choose to upgrade.
                </p>
                <ul className="text-xs text-zinc-400 space-y-0.5">
                  <li>• 3 Flash Drafts included during trial</li>
                  <li>• Mission Diagnostic, Corporate Scout, Cross-Collaboration, Impact Dashboard — all unlocked</li>
                  <li>• Add a card any time during the 14 days to keep Premium going past Day 14</li>
                </ul>
              </div>
              <Button
                onClick={handleStartTrial}
                disabled={trialLoading}
                className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm cta-hover px-6 py-6 w-full md:w-auto"
                data-testid="start-trial-btn"
              >
                {trialLoading ? (
                  <><div className="loading-spinner mr-2" /> Starting…</>
                ) : (
                  <><Sparkle weight="fill" className="w-4 h-4 mr-2" /> Start 14-Day Free Trial</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active trial banner */}
      {trialInfo?.trial_status === "active" && (
        <Card className="bg-emerald-500/5 border border-emerald-500/30" data-testid="trial-active-banner">
          <CardContent className="py-5">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <Sparkle weight="fill" className="w-5 h-5 text-emerald-400" />
                  <p className="font-semibold text-emerald-300">
                    Premium Trial — {trialInfo.days_remaining?.toFixed(1)} days remaining
                  </p>
                </div>
                <p className="text-xs text-zinc-400">
                  Flash Drafts used: <span className="text-white">{trialInfo.trial_flash_drafts_used}/3</span> · Card will <strong>NOT</strong> auto-charge · Trial ends {new Date(trialInfo.trial_ends_at).toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                </p>
              </div>
              <Button
                onClick={handleCancelTrial}
                disabled={cancelTrialLoading}
                variant="outline"
                className="border-zinc-700 text-zinc-300 rounded-sm hover:bg-zinc-900"
                data-testid="cancel-trial-btn"
              >
                {cancelTrialLoading ? "Cancelling…" : "Cancel Trial"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trial already used */}
      {trialInfo && ["ended","cancelled"].includes(trialInfo.trial_status) && user?.subscription_tier !== "pro" && (
        <Card className="bg-zinc-900/60 border border-zinc-800" data-testid="trial-used-banner">
          <CardContent className="py-4 flex items-center gap-3">
            <WarningCircle className="w-5 h-5 text-zinc-400 flex-shrink-0" />
            <p className="text-sm text-zinc-400">
              You've used your 14-day trial. Upgrade to Premium below to restore full access — your work is saved.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card 
            key={plan.id}
            className={`bg-zinc-950 border-zinc-800 relative ${plan.popular ? 'border-2 border-emerald-500/50 pricing-pro' : ''}`}
            data-testid={`plan-${plan.id}`}
          >
            {plan.popular && (
              <div className="absolute -top-3 right-4 badge-emerald px-3 py-1 text-xs">
                POPULAR
              </div>
            )}
            <CardHeader>
              <CardTitle className="text-lg">{plan.name}</CardTitle>
              <p className="text-sm text-zinc-400">{plan.description}</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                {plan.isCustom ? (
                  <span className="text-3xl font-mono font-bold">{plan.price}</span>
                ) : (
                  <>
                    <span className="text-4xl font-mono font-bold">${plan.price}</span>
                    {plan.price > 0 && <span className="text-zinc-500">/month</span>}
                  </>
                )}
              </div>
              
              <ul className="space-y-3">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                    <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Button 
                onClick={() => {
                  if (plan.ctaUrl) return navigate(plan.ctaUrl);
                  if (plan.price === 0) return navigate(user ? ((audience === "business" || audience === "franchisee") ? "/sponsorships" : "/grants") : "/");
                  return handleSubscribe(plan.id);
                }}
                disabled={plan.disabled || loading === plan.id}
                className={`w-full rounded-sm ${
                  plan.popular 
                    ? 'bg-emerald-500 text-black font-bold hover:bg-emerald-400 cta-hover' 
                    : 'bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800'
                }`}
                data-testid={`subscribe-${plan.id}`}
              >
                {loading === plan.id ? (
                  <>
                    <div className="loading-spinner mr-2" />
                    Processing...
                  </>
                ) : plan.cta}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Feature Comparison — only show for nonprofit audience */}
      {audience === "nonprofit" && (
      <Card className="bg-zinc-950 border-zinc-800 mt-12">
        <CardHeader>
          <CardTitle>Feature Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="text-center">Free</th>
                  <th className="text-center">Premium</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-zinc-300">Flash Drafts / month</td>
                  <td className="text-center">1</td>
                  <td className="text-center text-emerald-400">Unlimited</td>
                </tr>
                <tr>
                  <td className="text-zinc-300">Browse Grants</td>
                  <td className="text-center"><CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                  <td className="text-center"><CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="text-zinc-300">Application Tracker</td>
                  <td className="text-center"><CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                  <td className="text-center"><CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="text-zinc-300">Bookmarks</td>
                  <td className="text-center"><CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                  <td className="text-center"><CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="text-zinc-300">Post-Grant Manager</td>
                  <td className="text-center text-zinc-500">—</td>
                  <td className="text-center"><CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="text-zinc-300">Budget Tracking</td>
                  <td className="text-center text-zinc-500">—</td>
                  <td className="text-center"><CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="text-zinc-300">Flash Reports</td>
                  <td className="text-center text-zinc-500">—</td>
                  <td className="text-center"><CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Cancel Subscription - visible to Premium users */}
      {user?.subscription_tier === "pro" && (
        <Card className="bg-zinc-950 border-zinc-800 mt-8" data-testid="cancel-subscription-section">
          <CardContent className="py-6">
            {!showCancelConfirm ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-white">Manage Your Subscription</p>
                  <p className="text-sm text-zinc-400 mt-1">
                    You're currently on the <span className="text-emerald-400 font-medium">Premium</span> plan.
                    {user?.promo_code && " (via promo code)"}
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowCancelConfirm(true)}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 rounded-sm"
                  data-testid="cancel-subscription-btn"
                >
                  Cancel Subscription
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-sm p-4">
                  <WarningCircle weight="fill" className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-white">Are you sure you want to cancel?</p>
                    <p className="text-sm text-zinc-400 mt-1">
                      You'll lose access to Flash Drafts (unlimited), Post-Grant Manager, Budget Tracking, Flash Reports, 
                      Corporate Scout, and all other Premium features. You'll be downgraded to the Free plan immediately.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 justify-end">
                  <Button
                    variant="ghost"
                    onClick={() => setShowCancelConfirm(false)}
                    className="text-zinc-400 hover:text-white"
                    data-testid="cancel-keep-btn"
                  >
                    Keep Premium
                  </Button>
                  <Button
                    onClick={handleCancel}
                    disabled={cancelLoading}
                    className="bg-red-500 hover:bg-red-600 text-white font-bold rounded-sm"
                    data-testid="cancel-confirm-btn"
                  >
                    {cancelLoading ? "Cancelling..." : "Yes, Cancel Subscription"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sign-up modal triggered by logged-out "Start Trial" clicks. After
          successful sign-up, EmailAuthModal forwards to /pricing?start_trial=1
          and the auto-resume effect above kicks off the Stripe checkout. */}
      {showAuthForTrial && <EmailAuthModal onClose={() => setShowAuthForTrial(false)} />}
    </div>
  );
};

export default Pricing;
